// src/components/admin/batches/BatchList.tsx
import React from "react";
import { Batch } from "src/store/adminBatchStore";
import { Badge } from "src/components/ui/Badge";
import { User, AlertTriangle } from "lucide-react";

interface BatchListProps {
  batches: Batch[];
  selectedBatchId?: string | null;
  onSelectBatch: (batchId: string) => void;
  onEdit?: (batchId: string) => void;
  onCancel?: (batchId: string) => void;
  onComplete?: (batchId: string) => void;
  onAssignTutor?: (batchId: string) => void;
  onExtend?: (batchId: string) => void;
}

const BatchList: React.FC<BatchListProps> = ({
  batches,
  selectedBatchId,
  onSelectBatch,
}) => {
  if (batches.length === 0) {
    return (
      <div className="py-12 px-4 text-center bg-white rounded-xl border border-slate-200 shadow-sm">
        <p className="text-sm font-medium text-slate-700">No batches match your filter criteria.</p>
        <p className="text-xs text-slate-500 mt-1">Try adjusting your search query or status filter.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
      {/* Header Bar */}
      <div className="px-4 py-3 bg-[#faf9fe] border-b border-slate-200 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <span className="font-bold text-slate-900">Batches Queue</span>
          <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-mono font-semibold">
            {batches.length} displayed
          </span>
        </div>
        <span className="text-slate-500 hidden sm:inline">Click a batch to inspect and triage</span>
      </div>

      {/* Items list */}
      <div className="divide-y divide-slate-100 overflow-y-auto max-h-[680px]">
        {batches.map((batch) => {
          const isSelected = selectedBatchId === batch.id;
          const batchCode = `BAT-${batch.id.slice(0, 8).toUpperCase()}`;
          const total = batch.total_sessions ?? 0;
          const remaining = batch.remaining_sessions ?? 0;
          const completed = Math.max(0, total - remaining);
          const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
          const isLowBalance = remaining <= 2 && remaining > 0 && batch.status === "active";
          const hasNoTutor = !batch.default_tutor_id || !batch.tutor_name;

          return (
            <div
              key={batch.id}
              onClick={() => onSelectBatch(batch.id)}
              className={`p-4 transition cursor-pointer relative ${
                isSelected
                  ? "bg-purple-50/60 ring-1 ring-purple-500/80 shadow-2xs"
                  : "hover:bg-slate-50/80"
              }`}
            >
              {/* Row 1: Batch Name & Status Badge */}
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm text-slate-900">
                      {batch.name || batch.student_name || "Unnamed Batch"}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 flex flex-wrap items-center gap-1.5 mt-0.5">
                    {batch.student_name && (
                      <>
                        <span className="font-medium text-slate-800">
                          Student: {batch.student_name}
                        </span>
                        <span>&bull;</span>
                      </>
                    )}
                    <span className="text-slate-600">
                      {batch.subject_name || "General Curriculum"}
                    </span>
                    <span>&bull;</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700">
                      {batch.package_name || batch.package_type || "Booster"}
                    </span>
                  </div>
                </div>

                <div>
                  {hasNoTutor && batch.status === "active" ? (
                    <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-200 text-xs font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1"></span>
                      Needs Tutor
                    </Badge>
                  ) : isLowBalance ? (
                    <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-xs font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1"></span>
                      {remaining} Left
                    </Badge>
                  ) : batch.status === "active" ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs font-semibold">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1"></span>
                      Active
                    </Badge>
                  ) : batch.status === "completed" ? (
                    <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200 text-xs font-semibold">
                      Completed
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 text-xs capitalize">
                      {batch.status.replace("_", " ")}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Row 2: Assigned Tutor & Session Pacing */}
              <div className="flex items-center justify-between text-xs text-slate-500 pt-1 border-t border-slate-100/80">
                <div className="flex items-center gap-1.5 text-slate-700">
                  {hasNoTutor ? (
                    <span className="text-amber-800 font-semibold flex items-center gap-1">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                      Unassigned
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      <span className="font-medium">{batch.tutor_name}</span>
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 font-mono">
                  {total > 0 && (
                    <div className="w-16 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-1.5 rounded-full ${
                          isLowBalance
                            ? "bg-rose-500"
                            : percent === 100
                            ? "bg-emerald-500"
                            : "bg-purple-600"
                        }`}
                        style={{ width: `${Math.min(100, percent)}%` }}
                      ></div>
                    </div>
                  )}
                  <span className="text-slate-800 font-semibold">
                    {completed}/{total}
                  </span>
                  <span
                    className={
                      isLowBalance
                        ? "text-rose-600 font-bold"
                        : "text-purple-700 font-medium"
                    }
                  >
                    ({remaining} left)
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BatchList;
